import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins, Calendar, Music, QrCode, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AppSection = () => {
  const { t } = useTranslation();
  const [screenshotUrl, setScreenshotUrl] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`).then((r) => {
      setScreenshotUrl(r.data?.app_screenshot_url || '');
    }).catch(() => {});
  }, []);

  const features = [
    { icon: Coins, title: t('app.feature_coins'), description: t('app.feature_coins_desc') },
    { icon: Calendar, title: t('app.feature_reservations'), description: t('app.feature_reservations_desc') },
    { icon: Music, title: t('app.feature_dj'), description: t('app.feature_dj_desc') },
    { icon: QrCode, title: t('app.feature_qr'), description: t('app.feature_qr_desc') },
  ];

  return (
    <section 
      className="py-16 sm:py-24 bg-[#060606] relative overflow-hidden"
      data-testid="app-section"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 section-warm-edge opacity-50" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F0E8] mb-3"
            >
              {t('app.title')}
            </motion.h2>
            <p className="font-serif italic text-lg text-[#B9B2A8] mb-8">
              {t('app.subtitle')}
            </p>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-[#C9A84C]" />
                  </div>
                  <div>
                    <h3 className="font-display text-sm text-[#F5F0E8] uppercase tracking-wider">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-[#B9B2A8]">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://apps.apple.com/us/app/invasion-latina/id6758810207"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-[#F5F0E8] hover:border-[#C9A84C]/30 transition-colors"
                data-testid="app-download-ios"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] text-[#B9B2A8]">Télécharger sur</div>
                  <div className="text-sm font-medium">App Store</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.invasionlatina.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-[#F5F0E8] hover:border-[#C9A84C]/30 transition-colors"
                data-testid="app-download-android"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[10px] text-[#B9B2A8]">Disponible sur</div>
                  <div className="text-sm font-medium">Google Play</div>
                </div>
              </a>
            </div>
          </div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div className="relative w-64 sm:w-72">
              {/* Phone Frame */}
              <div className="aspect-[9/19] bg-[#1A1A1A] rounded-[3rem] p-3 shadow-2xl border border-white/10">
                <div className="w-full h-full bg-[#060606] rounded-[2.25rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1A1A1A] rounded-b-2xl z-10" />
                  {/* App Screenshot (admin-configurable via Settings → Screenshot de l'app) */}
                  {screenshotUrl ? (
                    <img
                      src={getImageUrl(screenshotUrl)}
                      alt="Invasion Latina App"
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#C9A84C]/20 via-[#060606] to-[#FF6A2A]/20">
                      <Smartphone className="w-12 h-12 text-[#C9A84C]/60 mb-3" />
                      <p className="text-xs text-[#B9B2A8]/80 uppercase tracking-widest">
                        Invasion Latina
                      </p>
                      <p className="font-display text-[#F5F0E8] text-lg mt-1">App mobile</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Glow Effect */}
              <div className="absolute inset-0 -z-10 blur-3xl opacity-30 bg-gradient-to-br from-[#C9A84C] to-[#FF6A2A]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppSection;
